// src/pages/about/changelog/add-changelog/ToolbarControl.jsx
import React from 'react';
import {
    Paper,
    IconButton,
    Box,
    FormControl,
    Select,
    MenuItem,
} from "@mui/material";
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatListBulleted,
    FormatListNumbered,
    FormatQuote,
    FormatAlignLeft,
    FormatAlignCenter,
    FormatAlignRight,
    FormatAlignJustify,
    Undo,
    Redo,
    Link as LinkIcon,
    InsertPhoto,
    Code,
    FormatClear,
    FormatStrikethrough,
    Highlight as HighlightIcon,
    FormatIndentIncrease,
    FormatIndentDecrease,
} from '@mui/icons-material';

// Komponen-komponen untuk toolbar
const MenuButton = ({ children, onClick, disabled = false, active = false, title }) => (
    <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={{
            backgroundColor: active ? 'action.selected' : 'transparent',
            '&:hover': {
                backgroundColor: 'action.hover',
            },
            minWidth: '32px',
            width: '32px',
            height: '32px',
        }}
        title={title}
    >
        {children}
    </IconButton>
);

const MenuSelect = ({ value, onChange, options, title }) => (
    <FormControl size="small" sx={{ minWidth: 120 }}>
        <Select
            value={value}
            onChange={onChange}
            size="small"
            sx={{ height: '32px', fontSize: '0.875rem' }}
            title={title}
        >
            {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </Select>
    </FormControl>
);

function ToolbarControl({ editor, onInsertLink, onInsertImage }) {
    if (!editor) {
        return null;
    }

    return (
        <Paper 
            elevation={0} 
            sx={{ 
                p: 1, 
                mb: 2, 
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fafafa',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5
            }}
        >
            {/* Undo/Redo */}
            <MenuButton
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().chain().focus().undo().run()}
                title="Undo"
            >
                <Undo fontSize="small" />
            </MenuButton>
            
            <MenuButton
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().chain().focus().redo().run()}
                title="Redo"
            >
                <Redo fontSize="small" />
            </MenuButton>
            
            <Box sx={{ width: '1px', backgroundColor: '#e0e0e0', mx: 0.5 }} />
            
            {/* Heading Levels */}
            <MenuSelect
                value={editor.getAttributes('heading').level || 'paragraph'}
                onChange={(e) => {
                    const level = e.target.value;
                    if (level === 'paragraph') {
                        editor.chain().focus().setParagraph().run();
                    } else {
                        editor.chain().focus().toggleHeading({ level: parseInt(level) }).run();
                    }
                }}
                options={[
                    { value: 'paragraph', label: 'Paragraph' },
                    { value: '1', label: 'Heading 1' },
                    { value: '2', label: 'Heading 2' },
                    { value: '3', label: 'Heading 3' },
                    { value: '4', label: 'Heading 4' },
                ]}
                title="Text Style"
            />
            
            <Box sx={{ width: '1px', backgroundColor: '#e0e0e0', mx: 0.5 }} />
            
            {/* Text Formatting */}
            <MenuButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                disabled={!editor.can().chain().focus().toggleBold().run()}
                active={editor.isActive('bold')}
                title="Bold"
            >
                <FormatBold fontSize="small" />
            </MenuButton>
            
            <MenuButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                disabled={!editor.can().chain().focus().toggleItalic().run()}
                active={editor.isActive('italic')}
                title="Italic"
            >
                <FormatItalic fontSize="small" />
            </MenuButton>
            
            <MenuButton
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                disabled={!editor.can().chain().focus().toggleUnderline().run()}
                active={editor.isActive('underline')}
                title="Underline"
            >
                <FormatUnderlined fontSize="small" />
            </MenuButton>
            
            <MenuButton
                onClick={() => editor.chain().focus().toggleStrike().run()}
                disabled={!editor.can().chain().focus().toggleStrike().run()}
                active={editor.isActive('strike')}
                title="Strikethrough"
            >
                <FormatStrikethrough fontSize="small" />
            </MenuButton>
            
            <MenuButton
                onClick={() => editor.chain().focus().toggleHighlight().run()}
                disabled={!editor.can().chain().focus().toggleHighlight().run()}
                active={editor.isActive('highlight')}
                title="Highlight"
            >
                <HighlightIcon fontSize="small" />
            </MenuButton>
            
            <Box sx={{ width: '1px', backgroundColor: '#e0e0e0', mx: 0.5 }} />
            
            {/* Lists */}
            <MenuButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                active={editor.isActive('bulletList')}
                title="Bullet List"
            >
                <FormatListBulleted fontSize="small" />
            </MenuButton>
            
            <MenuButton
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                active={editor.isActive('orderedList')}
                title="Numbered List"
            >
                <FormatListNumbered fontSize="small" />
            </MenuButton>
            
            <Box sx={{ width: '1px', backgroundColor: '#e0e0e0', mx: 0.5 }} />
            
            {/* Alignment */}
            <MenuButton
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                active={editor.isActive({ textAlign: 'left' })}
                title="Align Left"
            >
                <FormatAlignLeft fontSize="small" />
            </MenuButton>
            
            <MenuButton
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                active={editor.isActive({ textAlign: 'center' })}
                title="Align Center"
            >
                <FormatAlignCenter fontSize="small" />
            </MenuButton>
            
            <MenuButton
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                active={editor.isActive({ textAlign: 'right' })}
                title="Align Right"
            >
                <FormatAlignRight fontSize="small" />
            </MenuButton>
            
            <MenuButton
                onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                active={editor.isActive({ textAlign: 'justify' })}
                title="Justify"
            >
                <FormatAlignJustify fontSize="small" />
            </MenuButton>
            
            <Box sx={{ width: '1px', backgroundColor: '#e0e0e0', mx: 0.5 }} />
            
            {/* Special Formats */}
            <MenuButton
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                active={editor.isActive('blockquote')}
                title="Block Quote"
            >
                <FormatQuote fontSize="small" />
            </MenuButton>
            
            <MenuButton
                onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                active={editor.isActive('codeBlock')}
                title="Code Block"
            >
                <Code fontSize="small" />
            </MenuButton>
            
            <MenuButton
                onClick={onInsertLink}
                active={editor.isActive('link')}
                title="Insert Link"
            >
                <LinkIcon fontSize="small" />
            </MenuButton>
            
            <MenuButton
                onClick={onInsertImage}
                title="Insert Image"
            >
                <InsertPhoto fontSize="small" />
            </MenuButton>
            
            <Box sx={{ width: '1px', backgroundColor: '#e0e0e0', mx: 0.5 }} />
            
            {/* Indentation */}
            <MenuButton
                onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
                disabled={!editor.can().sinkListItem('listItem')}
                title="Increase Indent"
            >
                <FormatIndentIncrease fontSize="small" />
            </MenuButton>
            
            <MenuButton
                onClick={() => editor.chain().focus().liftListItem('listItem').run()}
                disabled={!editor.can().liftListItem('listItem')}
                title="Decrease Indent"
            >
                <FormatIndentDecrease fontSize="small" />
            </MenuButton>
            
            <Box sx={{ width: '1px', backgroundColor: '#e0e0e0', mx: 0.5 }} />
            
            {/* Clear Format */}
            <MenuButton
                onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                title="Clear Formatting"
            >
                <FormatClear fontSize="small" />
            </MenuButton>
        </Paper>
    );
}

export default ToolbarControl;